import { EXCEL_PT_BR, INTERNACIONAL } from '@equiview/domain';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { SemCadastro } from '../components/SemCadastro.js';
import {
  Aguardar,
  Botao,
  BotaoLink,
  CabecalhoPagina,
  classeCampo,
  classeTd,
  classeTh,
  Etiqueta,
  Painel,
  Seletor,
  Tabela,
} from '../components/ui/index.js';
import { formatarData, hojeIso } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import { baixarArquivo, csvLargo, csvLongo } from '../dados/exportar.js';
import type { Avaliacao } from '../dados/fonte.js';

type Situacao = 'todas' | 'abertas' | 'fechadas' | 'validadas';
const fechada = (a: Avaliacao) => a.status === 'fechada' || a.status === 'validada';

/**
 * Consulta e exportacao: o dado sai do aparelho numa planilha, sem pedir nada
 * ao dev. Formato longo para o R e o ICC; uma linha por ficha para o Excel.
 */
export function Dados() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(async (f) => {
    const [visitas, animais, avaliacoes] = await Promise.all([f.listarVisitas(), f.listarAnimais(), f.avaliacoes()]);
    const daVisita = new Set(visitas.map((v) => v.id));
    const fichas = avaliacoes.filter((a) => daVisita.has(a.visit_id));
    const ids = fichas.map((a) => a.id);
    const [medidas, observacoes, manejos] = await Promise.all([f.medidas(ids), f.observacoes(ids), f.manejos(ids)]);
    return { visitas, animais, fichas, medidas, observacoes, manejos };
  }, []);

  const [visita, setVisita] = useState('');
  const [nivel, setNivel] = useState<'todos' | '1' | '2'>('todos');
  const [situacao, setSituacao] = useState<Situacao>('todas');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [formato, setFormato] = useState<'excel' | 'internacional'>('excel');

  if (!equipe && !fonte.ficticia) return <SemCadastro demo={false} />;

  return (
    <Aguardar consulta={consulta}>
      {({ visitas, animais, fichas, medidas, observacoes, manejos }) => {
        const filtradas = fichas.filter(
          (a) =>
            (!visita || a.visit_id === visita) &&
            (nivel === 'todos' || String(a.protocol_level) === nivel) &&
            (situacao === 'todas' ||
              (situacao === 'abertas' && !fechada(a)) ||
              (situacao === 'fechadas' && a.status === 'fechada') ||
              (situacao === 'validadas' && a.status === 'validada')) &&
            (!de || a.visit_date >= de) &&
            (!ate || a.visit_date <= ate),
        );
        return (
          <Consulta
            {...{ visitas, animais, medidas, observacoes, filtradas, manejos }}
            filtros={{ visita, setVisita, nivel, setNivel, situacao, setSituacao, de, setDe, ate, setAte, formato, setFormato }}
          />
        );
      }}
    </Aguardar>
  );
}

function Consulta({
  visitas,
  animais,
  medidas,
  observacoes,
  filtradas,
  manejos,
  filtros: f,
}: {
  manejos: Parameters<typeof csvLargo>[3];
  visitas: Parameters<typeof csvLongo>[0]['visitas'];
  animais: Parameters<typeof csvLongo>[0]['animais'];
  medidas: Parameters<typeof csvLargo>[1];
  observacoes: Parameters<typeof csvLongo>[1];
  filtradas: Avaliacao[];
  filtros: {
    visita: string;
    setVisita: (v: string) => void;
    nivel: 'todos' | '1' | '2';
    setNivel: (v: 'todos' | '1' | '2') => void;
    situacao: Situacao;
    setSituacao: (v: Situacao) => void;
    de: string;
    setDe: (v: string) => void;
    ate: string;
    setAte: (v: string) => void;
    formato: 'excel' | 'internacional';
    setFormato: (v: 'excel' | 'internacional') => void;
  };
}) {
  const ids = useMemo(() => new Set(filtradas.map((a) => a.id)), [filtradas]);
  const nome = new Map(animais.map((a) => [a.id, a.name]));
  const numero = new Map(visitas.map((v) => [v.id, v.visit_number]));
  const contagem = new Map<string, number>();
  for (const m of medidas) if (m.value_num != null || m.not_applicable) contagem.set(m.assessment_id, (contagem.get(m.assessment_id) ?? 0) + 1);

  const formatoCsv = f.formato === 'excel' ? EXCEL_PT_BR : INTERNACIONAL;
  const ctx = { animais, visitas, avaliacoes: filtradas };
  const sufixo = `${hojeIso()}${f.formato === 'excel' ? '' : '-intl'}`;
  // No celular os filtros comecam recolhidos: abertos, ocupam uma tela antes das fichas.
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const ativos = [f.visita, f.de, f.ate, f.nivel !== 'todos', f.situacao !== 'todas'].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        titulo="Consulta e exportação"
        meta={<span>As fichas deste haras numa planilha, para o Excel ou para o R.</span>}
        acoes={<BotaoLink to="/repetibilidade">Relatório de repetibilidade</BotaoLink>}
      />

      <Painel
        titulo="Filtros"
        corpo={filtrosAbertos ? 'p-4' : 'p-0 md:p-4'}
        acoes={
          <Botao tamanho="sm" variante="discreto" className="md:hidden" aria-expanded={filtrosAbertos} onClick={() => setFiltrosAbertos((a) => !a)}>
            {filtrosAbertos ? 'Ocultar' : ativos ? `Mostrar (${ativos} ativo${ativos > 1 ? 's' : ''})` : 'Mostrar'}
          </Botao>
        }
      >
        <div className={filtrosAbertos ? '' : 'max-md:hidden'}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="space-y-1.5 text-sm">
              <span className="block font-medium">Visita</span>
              <select value={f.visita} onChange={(e) => f.setVisita(e.target.value)} className={classeCampo()}>
                <option value="">Todas</option>
                {visitas.map((v) => (
                  <option key={v.id} value={v.id}>
                    Nº {v.visit_number} · {formatarData(v.visit_date)}
                    {v.purpose === 'estudo_repetibilidade' ? ' · estudo' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1.5 text-sm">
              <span className="block font-medium">De</span>
              <input type="date" value={f.de} onChange={(e) => f.setDe(e.target.value)} className={classeCampo()} />
            </label>
            <label className="space-y-1.5 text-sm">
              <span className="block font-medium">Até</span>
              <input type="date" value={f.ate} onChange={(e) => f.setAte(e.target.value)} className={classeCampo()} />
            </label>
            <div className="space-y-1.5 text-sm">
              <span className="block font-medium">Nível</span>
              <Seletor
                rotulo="Nível"
                valor={f.nivel}
                onMudar={f.setNivel}
                opcoes={[
                  { valor: 'todos', conteudo: <span className="px-1 py-2">Todos</span> },
                  { valor: '1', conteudo: <span className="px-1 py-2">Censo</span> },
                  { valor: '2', conteudo: <span className="px-1 py-2">Completa</span> },
                ]}
              />
            </div>
          </div>
          <div className="mt-4 space-y-1.5 text-sm">
            <span className="block font-medium">Situação</span>
            <Seletor
              rotulo="Situação"
              valor={f.situacao}
              onMudar={f.setSituacao}
              opcoes={[
                { valor: 'todas', conteudo: <span className="px-1 py-2">Todas</span> },
                { valor: 'abertas', conteudo: <span className="px-1 py-2">Abertas</span> },
                { valor: 'fechadas', conteudo: <span className="px-1 py-2">Fechadas</span> },
                { valor: 'validadas', conteudo: <span className="px-1 py-2">Validadas</span> },
              ]}
            />
          </div>
        </div>
      </Painel>

      <Painel
        titulo={`Exportar ${filtradas.length} ficha(s)`}
        acoes={
          <Seletor
            rotulo="Formato"
            valor={f.formato}
            onMudar={f.setFormato}
            opcoes={[
              { valor: 'excel', conteudo: <span className="px-1 py-2">Excel em português</span> },
              { valor: 'internacional', conteudo: <span className="px-1 py-2">Internacional (R/Python)</span> },
            ]}
          />
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <Botao
            variante="principal"
            disabled={filtradas.length === 0}
            onClick={() => baixarArquivo(`equiview-fichas-${sufixo}.csv`, csvLargo(ctx, medidas.filter((m) => ids.has(m.assessment_id)), formatoCsv, manejos))}
          >
            Uma linha por ficha
          </Botao>
          <Botao
            disabled={filtradas.length === 0}
            onClick={() =>
              baixarArquivo(`equiview-observacoes-${sufixo}.csv`, csvLongo(ctx, observacoes.filter((o) => ids.has(o.assessment_id)), formatoCsv))
            }
          >
            Formato longo (todas as sessões)
          </Botao>
        </div>
        <p className="mt-3 text-xs text-apagado">
          "Uma linha por ficha" traz a primeira medição, com uma coluna por medida e lado, os índices e a Estação 1. O formato longo traz
          uma linha por observação, de todas as sessões (dupla medição e estudo) — é o do R e do ICC.
        </p>
      </Painel>

      <Painel titulo="Fichas" corpo="p-0">
        {filtradas.length === 0 ? (
          <p className="px-4 py-4 text-sm text-apagado">Nenhuma ficha com esses filtros.</p>
        ) : (
          <Tabela rotulo="Fichas" empilhar>
            <thead>
              <tr>
                <th scope="col" className={classeTh}>Animal</th>
                <th scope="col" className={classeTh}>Data</th>
                <th scope="col" className={`${classeTh} text-right`}>Visita</th>
                <th scope="col" className={classeTh}>Nível</th>
                <th scope="col" className={classeTh}>Situação</th>
                <th scope="col" className={`${classeTh} text-right`}>Medidas</th>
              </tr>
            </thead>
            <tbody>
              {filtradas
                .slice()
                .sort((x, y) => y.visit_date.localeCompare(x.visit_date))
                .map((a) => (
                  <tr key={a.id}>
                    <td className={classeTd}>
                      <Link to={`/animais/${a.animal_id}?ficha=${a.id}`} className="font-serif font-semibold text-destaque-texto hover:underline">
                        {nome.get(a.animal_id) ?? '—'}
                      </Link>
                    </td>
                    <td data-rotulo="" className={`${classeTd} numero`}>{formatarData(a.visit_date)}</td>
                    <td data-rotulo="Visita" className={`${classeTd} numero text-right`}>{numero.get(a.visit_id) ?? '—'}</td>
                    <td data-rotulo="" className={classeTd}>{a.protocol_level >= 2 ? 'Completa' : 'Censo'}</td>
                    <td data-rotulo="" className={classeTd}>
                      <Etiqueta tom={a.status === 'validada' || a.status === 'fechada' ? 'ok' : 'aviso'}>{a.status}</Etiqueta>
                    </td>
                    <td data-rotulo="Medidas" className={`${classeTd} numero text-right`}>{contagem.get(a.id) ?? 0}</td>
                  </tr>
                ))}
            </tbody>
          </Tabela>
        )}
      </Painel>
    </div>
  );
}
